import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const waLink = (phone, text) => {
  const d = String(phone || "").replace(/\D/g, "");
  const intl = d.startsWith("90") ? d : d.startsWith("0") ? `9${d}` : `90${d}`;
  return `https://wa.me/${intl}?text=${encodeURIComponent(text)}`;
};
const dateTR = (s) => s.split("-").reverse().join(".");

export const OpenClassDeleteDialog = ({ c, open, onOpenChange, onDelete, onDeleted }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [deleted, setDeleted] = useState(false);
  const close = (v) => {
    if (!v) { setResult(null); onOpenChange(false); if (deleted) onDeleted(); return; }
    onOpenChange(v);
  };
  const run = async () => {
    setBusy(true);
    try {
      const r = await onDelete(c.booked > 0);
      toast.success("Açık ders kaldırıldı");
      if (r.cancelled?.length) { setDeleted(true); setResult(r); } else { onOpenChange(false); onDeleted(); }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const msg = (name) => `Merhaba ${name}, ${result.class.business_name} · ${dateTR(result.class.date)} ${result.class.start_time} dersimiz iptal edilmiştir. Rezervasyonunuz iptal edildi, paketinizden seans düşülmedi. Anlayışınız için teşekkür ederiz.`;
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="open-class-delete-dialog">
        <DialogHeader><DialogTitle>{result ? "Katılımcılara haber verin" : "Dersi silmek istediğinize emin misiniz?"}</DialogTitle></DialogHeader>
        {!result ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600"><b>{dateTR(c.date)} {c.start_time}–{c.end_time}</b> · {c.trainer_name} · {c.room_name}</p>
            {c.booked > 0 && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="open-class-delete-booked-warning">
                <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Bu derste {c.booked} kayıt var!</p>
                <p className="mt-1 text-xs">{c.attendees.map((a) => a.member_name).join(", ")}</p>
                <p className="mt-2 text-xs">Silerseniz bu rezervasyonlar otomatik iptal edilir (paketten seans düşülmez) ve uygulamayı kullanan üyelere bildirim gönderilir.</p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-xl" onClick={() => close(false)} data-testid="open-class-delete-cancel">Vazgeç</Button>
              <Button className="rounded-xl bg-red-600 hover:bg-red-700" disabled={busy} onClick={run} data-testid="open-class-delete-confirm">
                {c.booked > 0 ? "Yine de Sil ve İptal Et" : "Sil"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-slate-600">{result.cancelled.length} rezervasyon iptal edildi. WhatsApp ile tek tek bilgi verebilirsiniz:</p>
            {result.cancelled.map((m, i) => (
              <div key={i} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2">
                <span className="text-sm font-medium text-navy">{m.member_name}</span>
                {m.phone ? (
                  <a href={waLink(m.phone, msg(m.member_name))} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-lg bg-[#25D366] hover:bg-[#1ebe5b] px-2.5 py-1.5 text-xs font-semibold text-white transition-colors"
                    data-testid={`open-class-delete-wa-${i}`}><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>
                ) : <span className="text-xs text-grey">Telefon yok</span>}
              </div>
            ))}
            <div className="flex justify-end pt-1"><Button className="rounded-xl" onClick={() => close(false)} data-testid="open-class-delete-done">Tamam</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
